import { format, formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { 
  parseISO, 
  isToday as isTodayFns, 
  isYesterday as isYesterdayFns,
  differenceInDays,
  startOfDay,
  parse
} from 'date-fns';

/**
 * Safely parse a date string as UTC, ensuring 'Z' suffix if not present
 * Handles backend DateTime serialization that may omit 'Z'
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Date} Parsed Date object
 */
const ensureUTCParse = (dateInput) => {
  if (!dateInput) return null;
  
  // If already a Date object, return it
  if (dateInput instanceof Date) return dateInput;
  
  // If string, ensure it's treated as UTC by appending 'Z' if missing
  let dateStr = dateInput;
  if (typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T00:00:00')) {
    // Only add Z if it looks like an ISO datetime string without timezone info
    if (dateStr.includes('T')) {
      dateStr = dateStr + 'Z';
    }
  }
  
  return parseISO(dateStr);
};

/**
 * DateTimeHelper - Frontend timezone utility
 * Mirrors backend DateTimeHelper.cs pattern
 * 
 * All API datetime responses are assumed to be in UTC ISO format (e.g., "2026-04-22T12:30:00Z")
 * All conversions use the business timezone from backend (stored in localStorage during login)
 */

/**
 * Get the business timezone from localStorage
 * Falls back to browser timezone if not available (edge case: direct URL access before login)
 * @returns {string} IANA timezone identifier (e.g., "Asia/Kolkata")
 */
export const getBusinessTimezone = () => {
  const timezone = localStorage.getItem('businessTimezone');
  
  if (!timezone) {
    // Fallback to browser timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Log warning in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('BusinessTimezone not found in localStorage, falling back to browser timezone:', browserTimezone);
    }
    
    return browserTimezone;
  }
  
  return timezone;
};

/**
 * Convert UTC date to business timezone and format it
 * @param {Date|string} utcDate - UTC date (Date object or ISO string)
 * @param {string} formatString - date-fns format string (e.g., 'dd MMM yyyy', 'HH:mm a')
 * @returns {string} Formatted date string in business timezone
 */
export const formatInBusinessTimezone = (utcDate, formatString) => {
  if (!utcDate) return '';
  
  try {
    // Parse if string, otherwise use Date object (ensure UTC parsing)
    const date = ensureUTCParse(utcDate);
    const timezone = getBusinessTimezone();
    
    return formatInTimeZone(date, timezone, formatString);
  } catch (error) {
    console.error('Error formatting date in business timezone:', error, { utcDate, formatString });
    return '';
  }
};

/**
 * Parse a date string in business timezone and convert to UTC
 * Useful for date inputs that need to be sent to backend
 * @param {string} dateString - Date string in business timezone
 * @param {string} formatString - date-fns format string to parse with
 * @returns {Date} UTC Date object
 */
export const parseBusinessDateToUTC = (dateString, formatString) => {
  if (!dateString) return null;
  
  try {
    const timezone = getBusinessTimezone();
    const parsedDate = parse(dateString, formatString, new Date());
    
    // Convert from business timezone to UTC
    return fromZonedTime(parsedDate, timezone);
  } catch (error) {
    console.error('Error parsing business date to UTC:', error, { dateString, formatString });
    return null;
  }
};

/**
 * Get current time in business timezone
 * Mirrors backend DateTimeHelper.GetBusinessNow()
 * @returns {Date} Current time as Date object in business timezone
 */
export const getBusinessNow = () => {
  const timezone = getBusinessTimezone();
  return toZonedTime(new Date(), timezone);
};

/**
 * Get today's date (start of day) in business timezone
 * Mirrors backend DateTimeHelper.GetBusinessToday()
 * @returns {Date} Today's date (00:00:00) in business timezone
 */
export const getBusinessToday = () => {
  const now = getBusinessNow();
  return startOfDay(now);
};

/**
 * Check if a UTC date is "today" in business timezone
 * @param {Date|string} utcDate - UTC date to check
 * @returns {boolean} True if the date is today in business timezone
 */
export const isToday = (utcDate) => {
  if (!utcDate) return false;
  
  try {
    const date = ensureUTCParse(utcDate);
    const timezone = getBusinessTimezone();
    const zonedDate = toZonedTime(date, timezone);
    const todayInBusinessTz = getBusinessToday();
    
    return isTodayFns(zonedDate) && startOfDay(zonedDate).getTime() === todayInBusinessTz.getTime();
  } catch (error) {
    console.error('Error checking isToday:', error, { utcDate });
    return false;
  }
};

/**
 * Check if a UTC date is "yesterday" in business timezone
 * @param {Date|string} utcDate - UTC date to check
 * @returns {boolean} True if the date is yesterday in business timezone
 */
export const isYesterday = (utcDate) => {
  if (!utcDate) return false;
  
  try {
    const date = ensureUTCParse(utcDate);
    const timezone = getBusinessTimezone();
    const zonedDate = toZonedTime(date, timezone);
    
    return isYesterdayFns(zonedDate);
  } catch (error) {
    console.error('Error checking isYesterday:', error, { utcDate });
    return false;
  }
};

/**
 * Smart timestamp formatting for MessageScreen
 * - Today: HH:mm a (e.g., "02:30 PM")
 * - Yesterday: "Yesterday"
 * - Within 7 days: Day name (e.g., "Monday")
 * - Older: MMM dd (e.g., "Apr 15")
 * @param {Date|string} utcDate - UTC date to format
 * @returns {string} Formatted timestamp
 */
export const smartFormatTimestamp = (utcDate) => {
  if (!utcDate) return '';
  
  try {
    const date = ensureUTCParse(utcDate);
    const timezone = getBusinessTimezone();
    const zonedDate = toZonedTime(date, timezone);
    const now = getBusinessNow();
    
    // Check if today
    if (isTodayFns(zonedDate)) {
      return formatInTimeZone(date, timezone, 'HH:mm a');
    }
    
    // Check if yesterday
    if (isYesterdayFns(zonedDate)) {
      return 'Yesterday';
    }
    
    // Check if within 7 days
    const daysDiff = differenceInDays(now, zonedDate);
    if (daysDiff < 7 && daysDiff >= 0) {
      return formatInTimeZone(date, timezone, 'EEEE'); // Day name (e.g., "Monday")
    }
    
    // Older than 7 days
    return formatInTimeZone(date, timezone, 'MMM dd');
  } catch (error) {
    console.error('Error in smartFormatTimestamp:', error, { utcDate });
    return '';
  }
};

/**
 * Check if a UTC date is before business "now"
 * Useful for checking if something is overdue
 * @param {Date|string} utcDate - UTC date to check
 * @returns {boolean} True if the date is in the past (business timezone)
 */
export const isBeforeNow = (utcDate) => {
  if (!utcDate) return false;
  
  try {
    const date = ensureUTCParse(utcDate);
    const now = getBusinessNow();
    
    return date < now;
  } catch (error) {
    console.error('Error checking isBeforeNow:', error, { utcDate });
    return false;
  }
};

/**
 * Check if a UTC date is after business "now"
 * Useful for checking if something is upcoming
 * @param {Date|string} utcDate - UTC date to check
 * @returns {boolean} True if the date is in the future (business timezone)
 */
export const isAfterNow = (utcDate) => {
  if (!utcDate) return false;
  
  try {
    const date = ensureUTCParse(utcDate);
    const now = getBusinessNow();
    
    return date > now;
  } catch (error) {
    console.error('Error checking isAfterNow:', error, { utcDate });
    return false;
  }
};

/**
 * Get the month number (1-12) from business "now"
 * @returns {number} Current month in business timezone (1-12)
 */
export const getBusinessMonth = () => {
  const now = getBusinessNow();
  return now.getMonth() + 1; // JavaScript months are 0-indexed
};

/**
 * Get the year from business "now"
 * @returns {number} Current year in business timezone
 */
export const getBusinessYear = () => {
  const now = getBusinessNow();
  return now.getFullYear();
};

/**
 * Convert UTC date to business timezone Date object
 * Useful for date comparisons
 * @param {Date|string} utcDate - UTC date to convert
 * @returns {Date} Date object in business timezone
 */
export const toBusinessTimezone = (utcDate) => {
  if (!utcDate) return null;
  
  try {
    const date = ensureUTCParse(utcDate);
    const timezone = getBusinessTimezone();
    
    return toZonedTime(date, timezone);
  } catch (error) {
    console.error('Error converting to business timezone:', error, { utcDate });
    return null;
  }
};

export default {
  getBusinessTimezone,
  formatInBusinessTimezone,
  parseBusinessDateToUTC,
  getBusinessNow,
  getBusinessToday,
  isToday,
  isYesterday,
  smartFormatTimestamp,
  isBeforeNow,
  isAfterNow,
  getBusinessMonth,
  getBusinessYear,
  toBusinessTimezone,
};
